import { Table, type TableColumn } from '@hgc/ui';
import type { JSX } from 'react';

import type { MetricRow } from './rows';

/*
 * 每一列都必须给 `render`。Table 在"既没有 `render` 也没有 `sortValue`"时渲染空单元格 ——
 * 那是为"只需要排序、不需要显示"的列留的口子，而不是默认行为。
 * 这里的列都不参与排序（遥测读数的顺序是刻意排的），所以显示内容必须显式给出。
 */
const COLUMNS: readonly TableColumn<MetricRow>[] = [
  { key: 'label', header: '项目', width: '12rem', render: (row) => row.label },
  {
    key: 'value',
    header: '读数',
    align: 'end',
    render: (row) => (
      /*
       * 数值用等宽字体：一列数字只有在字宽相同时才能被眼睛竖着扫过去，
       * 而"扫一眼发现某个数字不对"正是遥测面板的全部用途。
       */
      <span className="font-mono tabular-nums">{row.value}</span>
    ),
  },
  { key: 'unit', header: '单位', width: '4rem', render: (row) => <span className="text-fg-muted">{row.unit}</span> },
  { key: 'note', header: '说明', render: (row) => <span className="text-xs text-fg-subtle">{row.note}</span> },
];

/**
 * 读数表。
 *
 * 不做排序：这一张表的顺序是**刻意排的**（按子系统、按重要性），
 * 让用户把它按字母序重排只会得到一份更难读的清单。要按值排序的场景在参数表，不在遥测。
 */
export function MetricTable({
  rows,
  label,
}: {
  readonly rows: readonly MetricRow[];
  readonly label: string;
}): JSX.Element {
  return (
    <Table
      rows={rows}
      columns={COLUMNS}
      getRowKey={(row) => row.id}
      density="compact"
      /* 只给 `aria-label`，不再另加 `<caption>`：两者同时给会让读屏器把同一句话读两遍 */
      aria-label={label}
    />
  );
}
